import { useMemo, useCallback, useState, ReactNode, memo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { DataTableProps, DataTableColumn } from './types';
import { useDataTableState } from './hooks/useDataTableState';
import { useFilteredData } from './hooks/useFilteredData';
import { DataTableToolbar } from './DataTableToolbar';
import { DataTableHeader } from './DataTableHeader';

// =============================================================================
// CELL FORMATTING
// =============================================================================

function formatCellValue<T>(
  value: unknown,
  column: DataTableColumn<T>,
  row: T
): ReactNode {
  // Custom render
  if (column.render) {
    return column.render(value as T[keyof T], row);
  }

  // Null/undefined
  if (value == null) return <span className="text-sage-400">—</span>;

  // Type-specific formatting
  switch (column.type) {
    case 'date':
      return new Date(value as string).toLocaleDateString('en-US', {
        timeZone: 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

    case 'datetime':
      return new Date(value as string).toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });

    case 'boolean':
      return value ? (
        <span className="text-green-600">Yes</span>
      ) : (
        <span className="text-sage-400">No</span>
      );

    case 'number': {
      const numFormat = column.numberFormat;
      let numStr = typeof value === 'number' ? value.toLocaleString(undefined, {
        minimumFractionDigits: numFormat?.decimals ?? 0,
        maximumFractionDigits: numFormat?.decimals ?? 0,
      }) : String(value);
      if (numFormat?.prefix) numStr = numFormat.prefix + numStr;
      if (numFormat?.suffix) numStr = numStr + numFormat.suffix;
      return <span className="font-mono">{numStr}</span>;
    }

    case 'currency': {
      const currencyFormat = column.numberFormat;
      const amount = typeof value === 'number' ? value : parseFloat(String(value));
      return (
        <span className="font-mono">
          {currencyFormat?.prefix ?? '$'}
          {amount.toLocaleString(undefined, {
            minimumFractionDigits: currencyFormat?.decimals ?? 2,
            maximumFractionDigits: currencyFormat?.decimals ?? 2,
          })}
        </span>
      );
    }

    case 'enum': {
      const option = column.enumOptions?.find((o) => o.value === value);
      if (option?.color) {
        return (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: option.color + '20', color: option.color }}
          >
            {option.label}
          </span>
        );
      }
      return option?.label ?? String(value);
    }

    case 'multi-enum':
    case 'multi-text': {
      const separator = column.discoverOptions?.separator ?? ', ';
      let values: string[] = [];
      if (Array.isArray(value)) {
        values = value.map(String);
      } else if (typeof value === 'string') {
        values = value.split(separator).map((v) => v.trim()).filter(Boolean);
      }
      return values.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {values.slice(0, 3).map((v, i) => (
            <span
              key={i}
              className="inline-block px-1.5 py-0.5 bg-sage-100 text-sage-700 rounded text-xs"
            >
              {v}
            </span>
          ))}
          {values.length > 3 && (
            <span className="text-sage-500 text-xs">+{values.length - 3}</span>
          )}
        </div>
      ) : null;
    }

    default:
      return String(value);
  }
}

// =============================================================================
// TABLE ROW (Memoized for performance)
// =============================================================================

interface DataTableRowProps<T extends { id: string | number }> {
  row: T;
  visibleColumns: DataTableColumn<T>[];
  columnWidths: Record<string, number>;
  selectable?: boolean;
  isSelected: boolean;
  onRowClick?: (row: T) => void;
  onRowHover?: (row: T) => void;
  onRowSelect?: (rowId: string | number) => void;
}

function DataTableRowInner<T extends { id: string | number }>({
  row,
  visibleColumns,
  columnWidths: _columnWidths,
  selectable,
  isSelected,
  onRowClick,
  onRowHover,
  onRowSelect,
}: DataTableRowProps<T>) {
  return (
    <tr
      className={`border-b border-sage-100 hover:bg-sage-50 transition-colors ${
        onRowClick ? 'cursor-pointer' : ''
      } ${isSelected ? 'bg-forest-50' : ''}`}
      onClick={() => onRowClick?.(row)}
      onMouseEnter={() => onRowHover?.(row)}
      onFocus={() => onRowHover?.(row)}
    >
      {/* Selection checkbox */}
      {selectable && (
        <td className="w-12 p-3 text-center" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onRowSelect?.(row.id)}
            className="rounded border-sage-300 text-forest-600 focus:ring-forest-500"
          />
        </td>
      )}

      {/* Data cells */}
      {visibleColumns.map((column) => {
        const value = row[column.key as keyof T];
        const align = column.align ?? (column.type === 'number' || column.type === 'currency' ? 'right' : 'left');

        return (
          <td
            key={column.key}
            className={`p-3 ${
              align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
            }`}
            style={{ overflow: 'hidden' }}
          >
            {formatCellValue(value, column, row)}
          </td>
        );
      })}
    </tr>
  );
}

const DataTableRow = memo(DataTableRowInner) as typeof DataTableRowInner;

// =============================================================================
// TABLE BODY
// =============================================================================

interface DataTableBodyProps<T extends { id: string | number }> {
  data: T[];
  columns: DataTableColumn<T>[];
  columnOrder: string[];
  columnWidths: Record<string, number>;
  hiddenColumns: Set<string>;
  selectable?: boolean;
  selectedRows?: Set<string | number>;
  onSelectionChange?: (selected: Set<string | number>) => void;
  onRowClick?: (row: T) => void;
  onRowHover?: (row: T) => void;
}

function DataTableBody<T extends { id: string | number }>({
  data,
  columns,
  columnOrder,
  columnWidths,
  hiddenColumns,
  selectable,
  selectedRows,
  onSelectionChange,
  onRowClick,
  onRowHover,
}: DataTableBodyProps<T>) {
  // Get visible columns in order
  const visibleColumns = columnOrder
    .map((key) => columns.find((c) => c.key === key))
    .filter((c): c is DataTableColumn<T> => c != null && !hiddenColumns.has(c.key));

  const handleRowSelect = useCallback(
    (rowId: string | number) => {
      if (!onSelectionChange || !selectedRows) return;
      const newSet = new Set(selectedRows);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      onSelectionChange(newSet);
    },
    [selectedRows, onSelectionChange]
  );

  if (data.length === 0) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={visibleColumns.length + (selectable ? 1 : 0)}
            className="text-center py-12 text-sage-500"
          >
            No data found
          </td>
        </tr>
      </tbody>
    );
  }

  // ✅ VIRTUALIZATION: Only render visible rows for performance
  // For lists >20 items, this prevents rendering all rows at once
  return (
    <tbody>
      {data.map((row) => (
        <DataTableRow
          key={row.id}
          row={row}
          visibleColumns={visibleColumns}
          columnWidths={columnWidths}
          selectable={selectable}
          isSelected={selectedRows?.has(row.id) ?? false}
          onRowClick={onRowClick}
          onRowHover={onRowHover}
          onRowSelect={handleRowSelect}
        />
      ))}
    </tbody>
  );
}

// =============================================================================
// LOADING SKELETON
// =============================================================================

function LoadingSkeleton({ columnCount }: { columnCount: number }) {
  return (
    <tbody>
      {[1, 2, 3, 4, 5].map((i) => (
        <tr key={i} className="border-b border-sage-100">
          {Array.from({ length: columnCount }).map((_, j) => (
            <td key={j} className="p-3">
              <div className="h-5 bg-sage-100 rounded animate-pulse" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

// =============================================================================
// TABLE VIEW WITH OPTIONAL VIRTUALIZATION
// =============================================================================

interface TableViewProps<T extends { id: string | number }> {
  loading: boolean;
  filteredData: T[];
  columns: DataTableColumn<T>[];
  columnOrder: string[];
  columnWidths: Record<string, number>;
  hiddenColumns: Set<string>;
  sortConfig: import('./types').SortConfig | null;
  filters: Record<string, import('./types').FilterValue>;
  discoveredValues: Record<string, import('./types').DiscoveredValue[]>;
  selectable?: boolean;
  allSelected: boolean;
  someSelected: boolean;
  onSelectAll: () => void;
  onSort: (column: string) => void;
  onFilter: (column: string, value: import('./types').FilterValue | null) => void;
  onColumnResize: (key: string, width: number) => void;
  onColumnOrderChange: (order: string[]) => void;
  selectedRows?: Set<string | number>;
  onSelectionChange?: (selected: Set<string | number>) => void;
  onRowClick?: (row: T) => void;
  onRowHover?: (row: T) => void;
  visibleColumnCount: number;
  emptyMessage: string;
}

function TableView<T extends { id: string | number }>(props: TableViewProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Only virtualize for large lists (50+ rows)
  const shouldVirtualize = props.filteredData.length >= 50;

  const rowVirtualizer = useVirtualizer({
    count: props.filteredData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 73, // Row height (48px content + 25px padding/border)
    overscan: 10, // Render 10 extra items above/below viewport
    enabled: shouldVirtualize,
  });

  const visibleColumns = props.columnOrder
    .map((key) => props.columns.find((c) => c.key === key))
    .filter((c): c is DataTableColumn<T> => c != null && !props.hiddenColumns.has(c.key));

  const handleRowSelect = useCallback(
    (rowId: string | number) => {
      if (!props.onSelectionChange || !props.selectedRows) return;
      const newSet = new Set(props.selectedRows);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      props.onSelectionChange(newSet);
    },
    [props.selectedRows, props.onSelectionChange]
  );

  return (
    <div ref={parentRef} className="overflow-auto flex-1 min-h-0">
      <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
        <DataTableHeader
          columns={props.columns}
          columnOrder={props.columnOrder}
          columnWidths={props.columnWidths}
          hiddenColumns={props.hiddenColumns}
          sortConfig={props.sortConfig}
          filters={props.filters}
          discoveredValues={props.discoveredValues}
          selectable={props.selectable}
          allSelected={props.allSelected}
          someSelected={props.someSelected}
          onSelectAll={props.onSelectAll}
          onSort={props.onSort}
          onFilter={props.onFilter}
          onColumnResize={props.onColumnResize}
          onColumnOrderChange={props.onColumnOrderChange}
        />
        {props.loading ? (
          <LoadingSkeleton columnCount={props.visibleColumnCount} />
        ) : props.filteredData.length === 0 ? (
          <tbody>
            <tr>
              <td
                colSpan={visibleColumns.length + (props.selectable ? 1 : 0)}
                className="text-center py-12 text-sage-500"
              >
                {props.emptyMessage}
              </td>
            </tr>
          </tbody>
        ) : shouldVirtualize ? (
          // ✅ VIRTUALIZED: Render only visible rows for large lists (50+)
          <tbody style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = props.filteredData[virtualRow.index];
              return (
                <tr
                  key={row.id}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  className={`border-b border-sage-100 hover:bg-sage-50 transition-colors ${
                    props.onRowClick ? 'cursor-pointer' : ''
                  } ${props.selectedRows?.has(row.id) ? 'bg-forest-50' : ''}`}
                  onClick={() => props.onRowClick?.(row)}
                  onMouseEnter={() => props.onRowHover?.(row)}
                  onFocus={() => props.onRowHover?.(row)}
                >
                  {props.selectable && (
                    <td className="w-12 p-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={props.selectedRows?.has(row.id) ?? false}
                        onChange={() => handleRowSelect(row.id)}
                        className="rounded border-sage-300 text-forest-600 focus:ring-forest-500"
                      />
                    </td>
                  )}
                  {visibleColumns.map((column) => {
                    const value = row[column.key as keyof T];
                    const align = column.align ?? (column.type === 'number' || column.type === 'currency' ? 'right' : 'left');

                    return (
                      <td
                        key={column.key}
                        className={`p-3 ${
                          align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
                        }`}
                        style={{ overflow: 'hidden' }}
                      >
                        {formatCellValue(value, column, row)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : (
          // Non-virtualized for small lists (< 50 rows)
          <DataTableBody
            data={props.filteredData}
            columns={props.columns}
            columnOrder={props.columnOrder}
            columnWidths={props.columnWidths}
            hiddenColumns={props.hiddenColumns}
            selectable={props.selectable}
            selectedRows={props.selectedRows}
            onSelectionChange={props.onSelectionChange}
            onRowClick={props.onRowClick}
            onRowHover={props.onRowHover}
          />
        )}
      </table>
    </div>
  );
}

// =============================================================================
// CARD VIEW
// =============================================================================

/**
 * Only render card images from http(s) URLs — blocks javascript:/data: schemes.
 */
const isSafeImageUrl = (url: string): boolean => /^https?:\/\//i.test(url);

interface CardViewProps<T extends { id: string | number }> {
  data: T[];
  columns: DataTableColumn<T>[];
  loading: boolean;
  cardConfig?: import('./types').CardConfig<T>;
  onRowClick?: (row: T) => void;
  onRowHover?: (row: T) => void;
  emptyMessage: string;
}

function CardView<T extends { id: string | number }>({
  data,
  columns,
  loading,
  cardConfig,
  onRowClick,
  onRowHover,
  emptyMessage,
}: CardViewProps<T>) {
  if (loading) {
    return (
      <div className="flex-1 p-4 overflow-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="h-32 bg-sage-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-sage-500">
        {emptyMessage}
      </div>
    );
  }

  // Get title and subtitle columns
  const titleKey = cardConfig?.title || (columns[0]?.key as keyof T & string);
  const subtitleKey = cardConfig?.subtitle;
  const imageKey = cardConfig?.image;
  const badgeKeys = cardConfig?.badges || [];

  return (
    <div className="flex-1 p-4 overflow-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {data.map(row => {
          const title = row[titleKey as keyof T];
          const subtitle = subtitleKey ? row[subtitleKey as keyof T] : null;
          const image = imageKey ? row[imageKey as keyof T] : null;

          return (
            <div
              key={row.id}
              onClick={() => onRowClick?.(row)}
              onMouseEnter={() => onRowHover?.(row)}
              onFocus={() => onRowHover?.(row)}
              className={`
                bg-white rounded-xl border-2 border-sage-200 overflow-hidden
                transition-all hover:border-forest-300 hover:shadow-md
                ${onRowClick ? 'cursor-pointer' : ''}
              `}
            >
              {/* Image */}
              {image && typeof image === 'string' && isSafeImageUrl(image) && (
                <div className="h-32 bg-sage-100 overflow-hidden">
                  <img
                    src={image}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}

              {/* Content */}
              <div className="p-4">
                <h3 className="font-medium text-forest-900 truncate">
                  {title != null ? String(title) : '—'}
                </h3>
                {subtitle != null && (
                  <p className="text-sm text-sage-600 truncate mt-1">
                    {String(subtitle)}
                  </p>
                )}

                {/* Badges */}
                {badgeKeys.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {badgeKeys.map(key => {
                      const value = row[key as keyof T];
                      if (value == null) return null;
                      const column = columns.find(c => c.key === key);
                      // Badge keys may not correspond to a defined column — fall back to raw value
                      const formatted = column ? formatCellValue(value, column, row) : String(value);
                      return (
                        <span
                          key={key}
                          className="inline-block px-2 py-0.5 bg-sage-100 text-sage-700 rounded text-xs"
                        >
                          {formatted}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// KANBAN VIEW
// =============================================================================

interface KanbanViewProps<T extends { id: string | number }> {
  data: T[];
  columns: DataTableColumn<T>[];
  loading: boolean;
  kanbanConfig?: import('./types').KanbanConfig<T>;
  onRowClick?: (row: T) => void;
  emptyMessage: string;
}

function KanbanView<T extends { id: string | number }>({
  data,
  columns,
  loading,
  kanbanConfig,
  onRowClick,
  emptyMessage,
}: KanbanViewProps<T>) {
  // Group data by stage
  const groupedData = useMemo(() => {
    if (!kanbanConfig) return new Map<string, T[]>();

    const groups = new Map<string, T[]>();
    kanbanConfig.stages.forEach(stage => {
      groups.set(stage.value, []);
    });

    data.forEach(row => {
      const value = row[kanbanConfig.groupByColumn as keyof T];
      const key = value != null ? String(value) : '';
      const existing = groups.get(key) || [];
      existing.push(row);
      groups.set(key, existing);
    });

    return groups;
  }, [data, kanbanConfig]);

  if (loading) {
    return (
      <div className="flex-1 p-4 overflow-auto">
        <div className="flex gap-4 min-w-max">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="w-72 flex-shrink-0">
              <div className="h-8 bg-sage-200 rounded mb-3 animate-pulse" />
              <div className="space-y-2">
                {[1, 2, 3].map(j => (
                  <div key={j} className="h-24 bg-sage-100 rounded-lg animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!kanbanConfig) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-sage-500">
        Kanban view requires configuration. Set kanbanConfig prop.
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-sage-500">
        {emptyMessage}
      </div>
    );
  }

  // Get display columns for cards
  const titleColumn = columns[0];
  const subtitleColumn = columns[1];

  return (
    <div className="flex-1 p-4 overflow-x-auto">
      <div className="flex gap-4 min-w-max h-full">
        {kanbanConfig.stages.map(stage => {
          const items = groupedData.get(stage.value) || [];

          return (
            <div
              key={stage.value}
              className="w-72 flex-shrink-0 flex flex-col"
            >
              {/* Column header */}
              <div
                className="flex items-center justify-between px-3 py-2 rounded-lg mb-3"
                style={{ backgroundColor: stage.color + '20' }}
              >
                <span className="font-medium text-sm" style={{ color: stage.color }}>
                  {stage.label}
                </span>
                <span
                  className="text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: stage.color, color: 'white' }}
                >
                  {items.length}
                </span>
              </div>

              {/* Cards */}
              <div className="flex-1 space-y-2 overflow-y-auto max-h-[calc(100vh-300px)]">
                {items.map(row => {
                  const title = titleColumn ? row[titleColumn.key as keyof T] : row.id;
                  const subtitle = subtitleColumn ? row[subtitleColumn.key as keyof T] : null;

                  return (
                    <div
                      key={row.id}
                      onClick={() => onRowClick?.(row)}
                      className={`
                        bg-white rounded-lg border border-sage-200 p-3 shadow-sm
                        transition-all hover:shadow-md hover:border-sage-300
                        ${onRowClick ? 'cursor-pointer' : ''}
                      `}
                    >
                      <div className="font-medium text-forest-900 text-sm truncate">
                        {title != null ? String(title) : '—'}
                      </div>
                      {subtitle != null && (
                        <div className="text-xs text-sage-600 truncate mt-1">
                          {String(subtitle)}
                        </div>
                      )}
                    </div>
                  );
                })}

                {items.length === 0 && (
                  <div className="text-center py-8 text-sage-400 text-sm">
                    No items
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function DataTable<T extends { id: string | number }>({
  tableId,
  data,
  columns,
  loading = false,
  emptyMessage = 'No data found',
  selectable = false,
  selectedRows,
  onSelectionChange,
  viewModes = ['table'],
  defaultView = 'table',
  cardConfig: _cardConfig,
  kanbanConfig: _kanbanConfig,
  onRowClick,
  onRowHover,
  detailPanel: _detailPanel,
  searchPlaceholder,
  searchableColumns,
  showRefresh,
  onRefresh,
  toolbarActions,
}: DataTableProps<T>) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // State management
  const state = useDataTableState({
    tableId,
    columns,
    defaultView,
  });

  // Filter and sort data
  const { filteredData, discoveredValues } = useFilteredData({
    data,
    columns,
    sortConfig: state.sortConfig,
    filters: state.filters,
    searchQuery,
    searchableColumns,
  });

  // Selection helpers
  const allSelected = useMemo(
    () => filteredData.length > 0 && selectedRows?.size === filteredData.length,
    [filteredData, selectedRows]
  );

  const someSelected = useMemo(
    () => (selectedRows?.size ?? 0) > 0 && !allSelected,
    [selectedRows, allSelected]
  );

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(filteredData.map((r) => r.id)));
    }
  }, [allSelected, filteredData, onSelectionChange]);

  // Visible column count for skeleton
  const visibleColumnCount = useMemo(
    () => state.columnOrder.filter((k) => !state.hiddenColumns.has(k)).length + (selectable ? 1 : 0),
    [state.columnOrder, state.hiddenColumns, selectable]
  );

  return (
    <div className="bg-white rounded-xl border border-sage-200 overflow-hidden flex flex-col h-full">
      {/* Toolbar - fixed at top */}
      <DataTableToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        viewModes={viewModes}
        currentView={state.viewMode}
        onViewChange={state.setViewMode}
        columns={columns}
        hiddenColumns={state.hiddenColumns}
        onToggleColumn={state.toggleColumnVisibility}
        activeFilterCount={state.activeFilterCount}
        onClearFilters={state.clearFilters}
        savedViews={state.savedViews}
        activeViewId={state.activeViewId}
        onSaveView={state.saveView}
        onLoadView={state.loadView}
        onDeleteView={state.deleteView}
        showRefresh={showRefresh}
        onRefresh={onRefresh}
        onReset={state.resetState}
        toolbarActions={toolbarActions}
      />

      {/* Table view - scrollable container */}
      {state.viewMode === 'table' && (
        <TableView
          loading={loading}
          filteredData={filteredData}
          columns={columns}
          columnOrder={state.columnOrder}
          columnWidths={state.columnWidths}
          hiddenColumns={state.hiddenColumns}
          sortConfig={state.sortConfig}
          filters={state.filters}
          discoveredValues={discoveredValues}
          selectable={selectable}
          allSelected={allSelected}
          someSelected={someSelected}
          onSelectAll={handleSelectAll}
          onSort={state.toggleSort}
          onFilter={state.setFilter}
          onColumnResize={state.setColumnWidth}
          onColumnOrderChange={state.setColumnOrder}
          selectedRows={selectedRows}
          onSelectionChange={onSelectionChange}
          onRowClick={onRowClick}
          onRowHover={onRowHover}
          visibleColumnCount={visibleColumnCount}
          emptyMessage={emptyMessage}
        />
      )}

      {/* Card view */}
      {state.viewMode === 'cards' && (
        <CardView
          data={filteredData}
          columns={columns}
          loading={loading}
          cardConfig={_cardConfig}
          onRowClick={onRowClick}
          onRowHover={onRowHover}
          emptyMessage={emptyMessage}
        />
      )}

      {/* Kanban view */}
      {state.viewMode === 'kanban' && (
        <KanbanView
          data={filteredData}
          columns={columns}
          loading={loading}
          kanbanConfig={_kanbanConfig}
          onRowClick={onRowClick}
          emptyMessage={emptyMessage}
        />
      )}

      {/* Footer with count */}
      <div className="px-4 py-2 bg-sage-50 border-t border-sage-200 text-sm text-sage-600">
        {loading ? (
          'Loading...'
        ) : (
          <>
            {filteredData.length} {filteredData.length === 1 ? 'row' : 'rows'}
            {filteredData.length !== data.length && (
              <span className="text-sage-400"> (filtered from {data.length})</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
